/**
 * Employee Service
 * Handles church employee/staff management
 */

import { supabase } from '@/lib/supabase'

export type EmploymentType = 'full_time' | 'part_time' | 'contract' | 'casual'
export type EmploymentStatus = 'active' | 'on_leave' | 'suspended' | 'terminated'
export type PayFrequency = 'monthly' | 'weekly' | 'daily'

export interface Employee {
  id: string
  church_id: string
  employee_code: string
  first_name: string
  middle_name?: string
  last_name: string
  date_of_birth?: string
  gender?: string
  marital_status?: string

  // Employment
  employment_type: EmploymentType
  department?: string
  position_title: string
  job_grade?: string
  hire_date: string
  employment_status: EmploymentStatus

  // Statutory
  tin?: string
  ssnit_number?: string
  national_id?: string

  // Contact
  email?: string
  phone?: string
  address?: string

  // Emergency
  emergency_contact_name?: string
  emergency_contact_phone?: string

  // Banking
  bank_name?: string
  account_number?: string
  account_name?: string

  // Compensation
  basic_salary: number
  pay_frequency: PayFrequency
  currency: string

  is_active: boolean
  created_at?: string
  updated_at?: string
}

export interface CreateEmployeeInput {
  first_name: string
  middle_name?: string
  last_name: string
  date_of_birth?: string
  gender?: string
  marital_status?: string

  employment_type: EmploymentType
  department?: string
  position_title: string
  job_grade?: string
  hire_date: string

  tin?: string
  ssnit_number?: string
  national_id?: string

  email?: string
  phone?: string
  alternate_phone?: string
  address?: string
  city?: string
  region?: string

  emergency_contact_name?: string
  emergency_contact_phone?: string
  emergency_contact_relationship?: string

  bank_name?: string
  bank_branch?: string
  account_number?: string
  account_name?: string

  basic_salary: number
  pay_frequency?: PayFrequency
  currency?: string
  payment_method?: string

  notes?: string
}

export interface UpdateEmployeeInput extends Partial<CreateEmployeeInput> {
  employment_status?: EmploymentStatus
  termination_date?: string
  is_active?: boolean
}

export interface SalaryComponent {
  id?: string
  employee_id: string
  component_type: 'allowance' | 'deduction'
  component_code: string
  component_name: string
  description?: string
  amount: number
  calculation_type: 'fixed' | 'percentage'
  percentage_basis?: string
  is_taxable: boolean
  is_pensionable: boolean
  is_active: boolean
  start_date?: string
  end_date?: string
}

export interface EmployeeSummary {
  total_employees: number
  active_employees: number
  on_leave: number
  terminated: number
  full_time: number
  part_time: number
  contract: number
  total_monthly_cost: number
}

export class EmployeeService {

  /**
   * Get all employees for a church
   */
  static async getEmployees(
    churchId: string,
    filters?: {
      status?: EmploymentStatus
      department?: string
      employment_type?: EmploymentType
      search?: string
    }
  ): Promise<Employee[]> {
    let query = supabase
      .from('employees')
      .select('*')
      .eq('church_id', churchId)

    if (filters?.status) {
      query = query.eq('employment_status', filters.status)
    }

    if (filters?.department) {
      query = query.eq('department', filters.department)
    }

    if (filters?.employment_type) {
      query = query.eq('employment_type', filters.employment_type)
    }

    if (filters?.search) {
      query = query.or(`first_name.ilike.%${filters.search}%,last_name.ilike.%${filters.search}%,employee_code.ilike.%${filters.search}%`)
    }

    const { data, error } = await query.order('employee_code')

    if (error) throw error
    return data || []
  }

  /**
   * Get single employee by ID
   */
  static async getEmployee(employeeId: string): Promise<Employee | null> {
    const { data, error } = await supabase
      .from('employees')
      .select('*')
      .eq('id', employeeId)
      .single()

    if (error) return null
    return data
  }

  /**
   * Create new employee
   */
  static async createEmployee(
    churchId: string,
    employeeData: CreateEmployeeInput,
    userId: string
  ): Promise<Employee> {
    const { data, error } = await supabase
      .from('employees')
      .insert({
        church_id: churchId,
        ...employeeData,
        created_by: userId,
        updated_by: userId
      })
      .select()
      .single()

    if (error) throw error
    return data
  }

  /**
   * Update employee
   */
  static async updateEmployee(
    employeeId: string,
    employeeData: UpdateEmployeeInput,
    userId: string
  ): Promise<Employee> {
    const { data, error } = await supabase
      .from('employees')
      .update({
        ...employeeData,
        updated_by: userId,
        updated_at: new Date().toISOString()
      })
      .eq('id', employeeId)
      .select()
      .single()

    if (error) throw error
    return data
  }

  /**
   * Terminate employee
   */
  static async terminateEmployee(
    employeeId: string,
    terminationDate: string,
    userId: string,
    notes?: string
  ): Promise<Employee> {
    const { data, error } = await supabase
      .from('employees')
      .update({
        employment_status: 'terminated',
        termination_date: terminationDate,
        is_active: false,
        notes: notes,
        updated_by: userId,
        updated_at: new Date().toISOString()
      })
      .eq('id', employeeId)
      .select()
      .single()

    if (error) throw error
    return data
  }

  /**
   * Get employee summary statistics
   */
  static async getEmployeeSummary(churchId: string): Promise<EmployeeSummary> {
    const { data, error } = await supabase
      .from('employees')
      .select('employment_status, employment_type, basic_salary')
      .eq('church_id', churchId)

    if (error || !data) {
      return {
        total_employees: 0,
        active_employees: 0,
        on_leave: 0,
        terminated: 0,
        full_time: 0,
        part_time: 0,
        contract: 0,
        total_monthly_cost: 0
      }
    }

    const summary = data.reduce((acc, emp) => {
      acc.total_employees++

      // Count by status
      if (emp.employment_status === 'active') acc.active_employees++
      if (emp.employment_status === 'on_leave') acc.on_leave++
      if (emp.employment_status === 'terminated') acc.terminated++

      // Count by type
      if (emp.employment_type === 'full_time') acc.full_time++
      if (emp.employment_type === 'part_time') acc.part_time++
      if (emp.employment_type === 'contract') acc.contract++

      // Sum monthly cost (for active employees only)
      if (emp.employment_status === 'active') {
        acc.total_monthly_cost += emp.basic_salary || 0
      }

      return acc
    }, {
      total_employees: 0,
      active_employees: 0,
      on_leave: 0,
      terminated: 0,
      full_time: 0,
      part_time: 0,
      contract: 0,
      total_monthly_cost: 0
    })

    return summary
  }

  /**
   * Get employee salary components (allowances & deductions)
   */
  static async getSalaryComponents(employeeId: string): Promise<SalaryComponent[]> {
    const { data, error } = await supabase
      .from('employee_salary_components')
      .select('*')
      .eq('employee_id', employeeId)
      .order('component_type')
      .order('component_name')

    if (error) throw error
    return data || []
  }

  /**
   * Add salary component
   */
  static async addSalaryComponent(
    churchId: string,
    component: Omit<SalaryComponent, 'id'>,
    userId: string
  ): Promise<SalaryComponent> {
    const { data, error } = await supabase
      .from('employee_salary_components')
      .insert({
        ...component,
        church_id: churchId,
        created_by: userId
      })
      .select()
      .single()

    if (error) throw error
    return data
  }

  /**
   * Update salary component
   */
  static async updateSalaryComponent(
    componentId: string,
    updates: Partial<SalaryComponent>
  ): Promise<SalaryComponent> {
    const { data, error } = await supabase
      .from('employee_salary_components')
      .update(updates)
      .eq('id', componentId)
      .select()
      .single()

    if (error) throw error
    return data
  }

  /**
   * Delete salary component
   */
  static async deleteSalaryComponent(componentId: string): Promise<void> {
    const { error } = await supabase
      .from('employee_salary_components')
      .delete()
      .eq('id', componentId)

    if (error) throw error
  }

  /**
   * Get unique departments
   */
  static async getDepartments(churchId: string): Promise<string[]> {
    const { data, error } = await supabase
      .from('employees')
      .select('department')
      .eq('church_id', churchId)
      .not('department', 'is', null)

    if (error || !data) return []

    const departments = [...new Set(data.map(d => d.department).filter(Boolean))]
    return departments.sort()
  }

  /**
   * Get unique job titles
   */
  static async getJobTitles(churchId: string): Promise<string[]> {
    const { data, error } = await supabase
      .from('employees')
      .select('position_title')
      .eq('church_id', churchId)

    if (error || !data) return []

    const titles = [...new Set(data.map(d => d.position_title).filter(Boolean))]
    return titles.sort()
  }

  /**
   * Calculate total compensation for an employee
   */
  static async calculateTotalCompensation(employeeId: string): Promise<{
    basic_salary: number
    total_allowances: number
    total_deductions: number
    gross_monthly: number
    annual_cost: number
  }> {
    const employee = await this.getEmployee(employeeId)
    if (!employee) throw new Error('Employee not found')

    const components = await this.getSalaryComponents(employeeId)

    const allowances = components
      .filter(c => c.component_type === 'allowance' && c.is_active)
      .reduce((sum, c) => {
        if (c.calculation_type === 'percentage') {
          return sum + (employee.basic_salary * c.amount / 100)
        }
        return sum + c.amount
      }, 0)

    const deductions = components
      .filter(c => c.component_type === 'deduction' && c.is_active)
      .reduce((sum, c) => {
        if (c.calculation_type === 'percentage') {
          return sum + (employee.basic_salary * c.amount / 100)
        }
        return sum + c.amount
      }, 0)

    const grossMonthly = employee.basic_salary + allowances

    return {
      basic_salary: employee.basic_salary,
      total_allowances: Math.round(allowances * 100) / 100,
      total_deductions: Math.round(deductions * 100) / 100,
      gross_monthly: Math.round(grossMonthly * 100) / 100,
      annual_cost: Math.round(grossMonthly * 12 * 100) / 100
    }
  }
}

export default EmployeeService
